import React, { useState, useEffect } from 'react';
import {
  UserPlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  BellIcon,
  ClockIcon,
  KeyIcon,
  DeviceTabletIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import { userManagementAPI, employeePinAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import UserCreateModal from "../components/UserManagement/UserCreateModal";
import UserEditModal from "../components/UserManagement/UserEditModal";
import UserDetailModal from '../components/UserManagement/UserDetailModal';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [selectedUser, setSelectedUser] = useState(null);

  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [availableRoles, setAvailableRoles] = useState([]);
  
  // PIN management state
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinModalUser, setPinModalUser] = useState(null);
  const [pinData, setPinData] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [pinMessage, setPinMessage] = useState('');


  // Load available roles from the centralized role management system
  const loadRoles = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/centralized-roles/dropdown', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      
      if (result.success) {
        // Transform centralized roles to match expected format
        const roles = result.data.map(role => ({
          role: role.role_key,
          name: role.role_key,
          display_name: role.label,
          hierarchy_level: role.hierarchy_level
        }));
        setAvailableRoles(roles);
      } else {
        throw new Error(result.message || 'Failed to load roles');
      }
    } catch (error) {
      console.error("Error loading centralized roles:", error);
      // Fallback to basic system roles if API fails
      setAvailableRoles([
        { role: "admin", name: "admin", display_name: "Administrator", hierarchy_level: 10 },
        { role: "hr_manager", name: "hr_manager", display_name: "HR Manager", hierarchy_level: 8 },
        { role: "manager", name: "manager", display_name: "Manager", hierarchy_level: 6 },
        { role: "employee", name: "employee", display_name: "Employee", hierarchy_level: 1 }
      ]);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);


  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        role: selectedRole
      };
      
      const response = await userManagementAPI.getAll(params);
      setUsers(response.data.users);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, selectedRole]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const openModal = (mode, userData = null) => {
    setModalMode(mode);
    setSelectedUser(userData);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user? They will lose access to the system.')) return;
    
    try {
      await userManagementAPI.deactivate(userId);
      fetchUsers();
    } catch (error) {
      console.error('Error deactivating user:', error);
      alert('Failed to deactivate user');
    }
  };

  const handleResendInvitation = async (userId) => {
    try {
      await userManagementAPI.resendInvitation(userId);
      alert('Invitation email sent successfully');
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert('Failed to send invitation email');
    }
  };

  // PIN Management functions
  const openPinModal = (userData) => {
    setPinModalUser(userData);
    setPinData('');
    setPinMessage('');
    setShowPinModal(true);
  };

  const closePinModal = () => {
    setShowPinModal(false);
    setPinModalUser(null);
    setPinData('');
    setPinMessage('');
  };

  const handleSetPin = async (e) => {
    e.preventDefault();
    if (!pinModalUser?.employee_id) {
      setPinMessage('No employee record found for this user');
      return;
    }

    if (!pinData || pinData.length < 4 || pinData.length > 8) {
      setPinMessage('PIN must be 4-8 digits');
      return;
    }

    if (!/^\d+$/.test(pinData)) {
      setPinMessage('PIN must contain only numbers');
      return;
    }

    setPinLoading(true);
    setPinMessage('');

    try {
      await employeePinAPI.setPin(pinModalUser.employee_id, pinData);
      setPinMessage('PIN set successfully');
      
      // Update the user data to show PIN status
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === pinModalUser.id 
            ? { ...u, has_kiosk_pin: true }
            : u
        )
      );

      setTimeout(() => {
        closePinModal();
      }, 1500);
    } catch (error) {
      console.error('Error setting PIN:', error);
      setPinMessage(error.response?.data?.message || 'Failed to set PIN');
    } finally {
      setPinLoading(false);
    }
  };

  const handleRemovePin = async (userData) => {
    if (!userData?.employee_id) {
      alert('No employee record found for this user');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this user\'s kiosk PIN? They will need to set up a new PIN to use kiosk check-in.')) {
      return;
    }

    try {
      await employeePinAPI.removePin(userData.employee_id);
      
      // Update the user data to show PIN status
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === userData.id 
            ? { ...u, has_kiosk_pin: false }
            : u
        )
      );
      
      alert('Kiosk PIN removed successfully');
    } catch (error) {
      console.error('Error removing PIN:', error);
      alert(error.response?.data?.message || 'Failed to remove PIN');
    }
  };

  // Function to navigate to employee page
  const viewEmployee = (userData) => {
    if (userData.employee_id) {
      navigate(`/employees?employee_id=${userData.employee_id}`);
    }
  };

  // Only admins and HR managers can access user management
  if (!hasPermission(['admin', 'hr_manager'])) {
    return (
      <div className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-md">
        You don't have permission to access user management.
      </div>
    );
  }

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">User Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage system users, roles, and access permissions
          </p>
        </div>
        <button
          onClick={() => openModal('create')}
          className="btn-primary flex items-center space-x-2"
        >
          <UserPlusIcon className="w-4 h-4" />
          <span>Add User</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                Search Users
              </label>
              <div className="mt-1 relative">
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                  placeholder="Search by name or email..."
                />
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                Role
              </label>
              <select
                id="role"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="form-select mt-1"
              >
                <option value="">All Roles</option>
{availableRoles.map((role) => (
                <option key={role.name || role.role} value={role.name || role.role}>
                  {role.display_name || (role.name || role.role || '').charAt(0).toUpperCase() + (role.name || role.role || '').slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button type="submit" className="btn-primary w-full">
                Search
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.map((userData) => (
          <div key={userData.id} className="card hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 font-semibold text-lg">
                    {(userData.first_name && userData.first_name.charAt(0)) || (userData.email && userData.email.charAt(0)) || 'U'}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium text-gray-900 truncate">
                    {userData.first_name && userData.last_name 
                      ? `${userData.first_name} ${userData.last_name}`
                      : userData.email
                    }
                  </h3>
                  <p className="text-sm text-gray-600 truncate">{userData.email}</p>
                  {userData.employee_number && (
                    <div className="flex items-center space-x-2">
                      <p className="text-xs text-gray-500">#{userData.employee_number}</p>
                      {userData.employee_id && (
                        <button
                          onClick={() => viewEmployee(userData)}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                          title="View Employee Details"
                        >
                          <LinkIcon className="w-3 h-3" />
                          <span>View Employee</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex space-x-1 flex-shrink-0 ml-2">
                <button
                  onClick={() => openModal('view', userData)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title="View Details"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => openModal('edit', userData)}
                  className="p-2 text-gray-400 hover:text-blue-600"
                  title="Edit User"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                {userData.id !== user.id && (
                  <button
                    onClick={() => handleDeactivate(userData.id)}
                    className="p-2 text-gray-400 hover:text-red-600"
                    title="Deactivate User"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Additional User Information */}
            <div className="mt-4 space-y-2">
              <div className="flex items-center text-xs text-gray-500">
                <ShieldCheckIcon className="w-3 h-3 mr-1" />
                <span className="capitalize">
                  {availableRoles.find(r => r.role === userData.role)?.display_name || userData.role}
                  {availableRoles.find(r => r.role === userData.role)?.hierarchy_level && 
                    ` (Level ${availableRoles.find(r => r.role === userData.role).hierarchy_level})`
                  }
                </span>
              </div>
              <div className="flex items-center text-xs text-gray-500">
                <BellIcon className="w-3 h-3 mr-1" />
                <span>
                  {userData.notification_settings?.email ? 'Email notifications: ON' : 'Email notifications: OFF'}
                </span>
              </div>
              {userData.last_login && (
                <div className="flex items-center text-xs text-gray-500">
                  <ClockIcon className="w-3 h-3 mr-1" />
                  <span>Last login: {new Date(userData.last_login).toLocaleDateString()}</span>
                </div>
              )}
              
              {/* Kiosk PIN Status */}
              {userData.employee_id && (
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center">
                    <DeviceTabletIcon className="w-3 h-3 mr-1" />
                    <span>Kiosk PIN: </span>
                    <span className={`ml-1 font-medium ${
                      userData.has_kiosk_pin ? 'text-green-600' : 'text-orange-600'
                    }`}>
                      {userData.has_kiosk_pin ? 'SET' : 'NOT SET'}
                    </span>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => openPinModal(userData)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                      title={userData.has_kiosk_pin ? 'Change PIN' : 'Set PIN'}
                    >
                      {userData.has_kiosk_pin ? 'Change' : 'Set'}
                    </button>
                    {userData.has_kiosk_pin && (
                      <button
                        onClick={() => handleRemovePin(userData)}
                        className="text-red-600 hover:text-red-800 font-medium"
                        title="Remove PIN"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center mt-3">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  userData.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {userData.is_active ? 'ACTIVE' : 'INACTIVE'}
                </span>
                {!userData.last_login && (
                  <button
                    onClick={() => handleResendInvitation(userData.id)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                    title="Resend invitation email"
                  >
                    Resend Invite
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {users.length === 0 && !loading && (
        <div className="text-center py-12">
          <UserCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || selectedRole 
              ? 'Try adjusting your search filters.'
              : 'Get started by creating a new user.'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="btn-secondary disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="btn-secondary disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* User Detail Modals */}
      {showModal && modalMode === "view" && (
        <UserDetailModal
          user={selectedUser}
          onClose={closeModal}
          onEdit={() => openModal("edit", selectedUser)}
        />
      )}
      
      {showModal && modalMode === "create" && (
        <UserCreateModal
          onClose={closeModal}
          onSave={() => {
            fetchUsers();
            closeModal();
          }}
        />
      )}
      
      {showModal && modalMode === "edit" && (
        <UserEditModal
          user={selectedUser}
          onClose={closeModal}
          onSave={() => {
            fetchUsers();
            closeModal();
          }}
        />
      )}

      {/* PIN Management Modal */}
      {showPinModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {pinModalUser?.has_kiosk_pin ? 'Change' : 'Set'} Kiosk PIN
                </h3>
                <button
                  onClick={closePinModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Setting PIN for: <strong>
                    {pinModalUser?.first_name && pinModalUser?.last_name 
                      ? `${pinModalUser.first_name} ${pinModalUser.last_name}`
                      : pinModalUser?.email}
                  </strong>
                </p>
                {pinModalUser?.employee_number && (
                  <p className="text-xs text-gray-500">Employee #: {pinModalUser.employee_number}</p>
                )}
              </div>

              <form onSubmit={handleSetPin}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kiosk PIN (4-8 digits)
                  </label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    value={pinData}
                    onChange={(e) => setPinData(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    placeholder="Enter 4-8 digit PIN"
                    required
                    minLength="4"
                    maxLength="8"
                    disabled={pinLoading}
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                  <div className="flex">
                    <KeyIcon className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-800">PIN Guidelines</h4>
                      <ul className="mt-1 text-xs text-blue-700 list-disc list-inside">
                        <li>PIN must be 4-8 digits long</li>
                        <li>Use only numbers (0-9)</li>
                        <li>Employee can change this PIN later in their settings</li>
                        <li>PIN is used for kiosk check-in/out</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {pinMessage && (
                  <div className={`mb-4 p-3 rounded-md ${
                    pinMessage.includes('successfully') 
                      ? 'bg-green-50 border border-green-200 text-green-700' 
                      : 'bg-red-50 border border-red-200 text-red-700'
                  }`}>
                    <p className="text-sm">{pinMessage}</p>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closePinModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    disabled={pinLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    disabled={pinLoading}
                  >
                    {pinLoading ? 'Setting...' : 'Set PIN'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;